import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { Toaster } from "sonner";
import { queryClient, shouldPersistQuery } from "@/lib/query-client";
// MYID-D5 — i18n init must run before App mounts so the first paint has
// the resolved language available. The module is side-effecty (calls
// i18n.init() at module load) and react-i18next's initReactI18next
// plugin wires it to the React tree without needing a Provider.
import "@/i18n";
import App from "./App";
import "./globals.css";

// Legacy CSS (kept during migration, will be removed incrementally)
import "./index.css";

// MYID-D4 — persist a curated subset of query cache to localStorage.
// Only queries with `meta: { persist: true }` get dehydrated; the rest
// stay in-memory. localStorage (not IndexedDB / localforage) because
// the persisted surface is small (prefs + demographics) and the sync
// API is simpler.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const persister = createSyncStoragePersister({
  storage: typeof window !== "undefined" ? window.localStorage : undefined,
  key: "myCollabkareId:query-cache",
  // Versioned wrapper so a future cache-shape change invalidates the
  // persisted blob cleanly. Cast through `as any` because the
  // serialize/deserialize signature wants PersistedClient but our
  // wrapper is intentionally opaque (the inner `.data` IS the
  // PersistedClient — we just wrap it with a version sentinel).
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  serialize: ((data: unknown) => JSON.stringify({ v: 1, data })) as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  deserialize: ((raw: string) => {
    try {
      const parsed = JSON.parse(raw) as { v?: number; data?: unknown };
      if (parsed && typeof parsed === "object" && parsed.v === 1) return parsed.data;
    } catch {
      /* ignore */
    }
    return undefined;
  }) as any,
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: 1000 * 60 * 60 * 24, // 24h — same window as workbox API cache
        dehydrateOptions: {
          // shouldPersistQuery is typed against @tanstack/react-query's
          // Query<>; persist-client resolves its own copy of query-core
          // so the structural types mismatch on identity. Cast to align.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          shouldDehydrateQuery: shouldPersistQuery as any,
        },
      }}
    >
      <BrowserRouter>
        <App />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </PersistQueryClientProvider>
  </React.StrictMode>
);
